import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useLocation } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import Webcam from 'react-webcam'
import {
  fetchTeam,
  authFaceRecognition,
  fetchCenterAttendance,
  checkInOrOutCenter,
  setCenterAttendanceNotes,
  resetState,
  setSelectedEmployee,
  setEmployeeToken,
} from './centerAttendanceSlice'
import usePartialUpload from 'hooks/usePartialUpload'
import { Card, CardBody, Button, Input, Spinner, Row, Col } from 'reactstrap'
import {
  ArrowLeft,
  Camera,
  User,
  X,
  Search,
  LogOut,
  CheckCircle,
  Clock,
  AlertCircle,
  CheckCircle as CheckIcon,
  Image,
} from 'react-feather'
import moment from 'moment'
import './center-attendance.css'

const STEP_EMPLOYEE_LIST = 'EMPLOYEE_LIST'
const STEP_FACE_AUTH = 'FACE_AUTH'
const STEP_DASHBOARD = 'DASHBOARD'

// Custom Toast Component
const CustomToast = ({ message, variant = 'error', onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000)
    return () => clearTimeout(timer)
  }, [onClose])

  const bgColor = variant === 'error' ? '#fee2e2' : '#dcfce7'
  const textColor = variant === 'error' ? '#991b1b' : '#166534'
  const borderColor = variant === 'error' ? '#fecaca' : '#86efac'
  const icon =
    variant === 'error' ? <AlertCircle size={20} /> : <CheckIcon size={20} />

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        backgroundColor: bgColor,
        color: textColor,
        padding: '16px',
        borderRadius: '12px',
        border: `1px solid ${borderColor}`,
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        fontSize: '14px',
        fontWeight: '500',
        animation: 'slideIn 0.3s ease-out',
      }}
    >
      {icon}
      <span>{message}</span>
    </div>
  )
}

export default function CenterAttendance() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const { enqueueSnackbar } = useSnackbar()
  const { uploadFile } = usePartialUpload()

  const [step, setStep] = useState(STEP_EMPLOYEE_LIST)
  const [image, setImage] = useState(null)
  const [note, setNote] = useState('')
  const [loading, setLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [toast, setToast] = useState(null)
  const [photoModal, setPhotoModal] = useState({
    isOpen: false,
    photoUrl: '',
    type: '',
  })
  const webcamRef = useRef(null)

  const {
    team,
    teamLoading,
    selectedEmployee,
    employeeToken,
    attendanceData,
    attendanceLoading,
    attendanceCurrentPage,
    attendanceTotalPages,
    attendanceHasMore,
  } = useSelector((state) => state.centerAttendance)

  // --- Effects ---

  useEffect(() => {
    if (step === STEP_EMPLOYEE_LIST) {
      dispatch(fetchTeam({ page: 0, size: 100 }))
      dispatch(resetState())
    }
  }, [dispatch, step])

  // AUTO SELECT EMPLOYEE FROM HOMEPAGE (IF APPLICABLE)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const autoSelectId = location.state?.autoSelectUserId
    if (autoSelectId && team.length > 0 && step === STEP_EMPLOYEE_LIST) {
      const employeeToSelect = team.find(
        (emp) =>
          String(emp.userId) === String(autoSelectId) ||
          String(emp.id) === String(autoSelectId),
      )

      if (employeeToSelect) {
        handleSelectEmployee(employeeToSelect)
        // Bersihkan state agar tidak otomatis masuk dashboard
        // berulang kali setiap kembali ke halaman list
        const newState = { ...location.state }
        delete newState.autoSelectUserId
        navigate(location.pathname, { replace: true, state: newState })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [team, location.state, step, navigate])

  useEffect(() => {
    if (step === STEP_EMPLOYEE_LIST) {
      const debounceTimer = setTimeout(() => {
        dispatch(fetchTeam({ page: 0, size: 100, s: searchTerm }))
      }, 300)
      return () => clearTimeout(debounceTimer)
    }
  }, [searchTerm, dispatch, step])

  useEffect(() => {
    if (employeeToken && step === STEP_EMPLOYEE_LIST) {
      setStep(STEP_DASHBOARD)
      dispatch(fetchCenterAttendance({}))
    }
  }, [employeeToken, step, dispatch])

  // Handle browser back button
  useEffect(() => {
    const handlePopState = (event) => {
      if (step === STEP_DASHBOARD) {
        // Prevent default back navigation
        event.preventDefault()
        // Return to employee selection screen
        setStep(STEP_EMPLOYEE_LIST)
        dispatch(resetState())
        setImage(null)
        // Push state back to prevent actual navigation
        window.history.pushState(null, '', window.location.pathname)
      }
    }

    // Add state to history when entering dashboard
    if (step === STEP_DASHBOARD) {
      window.history.pushState(null, '', window.location.pathname)
    }

    window.addEventListener('popstate', handlePopState)

    return () => {
      window.removeEventListener('popstate', handlePopState)
    }
  }, [step, dispatch])

  // --- Handlers ---

  const handleSelectEmployee = async (employee) => {
    dispatch(setSelectedEmployee(employee))
    // Auto-call authFaceRecognition with empty photo string
    try {
      setLoading(true)
      const result = await dispatch(
        authFaceRecognition({
          photo: '',
          employeeUserId: employee.userId,
        }),
      ).unwrap()

      if (!result?.verificationDump?.verified) {
        const errorMsg =
          result?.verificationDump?.verificationErrMsg || 'Verifikasi gagal'
        throw new Error(errorMsg)
      }

      setToast({ message: 'Verifikasi berhasil', variant: 'success' })
    } catch (error) {
      console.error('Face recognition error:', error)
      const errorMessage =
        error?.message || 'Verifikasi gagal - Silakan coba lagi'
      setToast({ message: errorMessage, variant: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const handleBack = () => {
    if (step === STEP_DASHBOARD) {
      // Navigate back to Absensi Center (employee selection screen)
      setStep(STEP_EMPLOYEE_LIST)
      dispatch(resetState())
      setImage(null)
    } else {
      setStep(STEP_EMPLOYEE_LIST)
      dispatch(resetState())
      setImage(null)
    }
  }

  const handleExit = () => {
    // Navigate back to previous page or dashboard
    // Since we are in a "fullscreen" overlay, we might want to just go back in history
    window.history.back()
  }

  const handleCaptureAuth = async () => {
    if (!webcamRef.current) return
    const imageSrc = webcamRef.current.getScreenshot()
    setImage(imageSrc)

    try {
      setLoading(true)
      const file = dataURLtoFile(imageSrc, `auth-${Date.now()}.jpg`)

      const uploaded = await uploadFile(
        file,
        () => { },
        'CENTER_AUTH',
        'ATTENDANCE',
        true,
      )

      if (!uploaded?.url) {
        throw new Error('Gagal mengunggah foto')
      }

      const result = await dispatch(
        authFaceRecognition({
          photo: uploaded.url,
          employeeUserId: selectedEmployee.userId,
        }),
      ).unwrap()

      if (!result?.verificationDump?.verified) {
        const errorMsg =
          result?.verificationDump?.verificationErrMsg ||
          'Verifikasi wajah gagal'
        throw new Error(errorMsg)
      }

      setToast({ message: 'Verifikasi berhasil', variant: 'success' })
    } catch (error) {
      const errorMessage = error || 'Verifikasi gagal - Silakan coba lagi'
      setToast({ message: errorMessage, variant: 'error' })
      setImage(null)
    } finally {
      setLoading(false)
    }
  }

  const handleCheckInOut = async () => {
    if (!webcamRef.current) return
    const imageSrc = webcamRef.current.getScreenshot()

    try {
      setLoading(true)
      const file = dataURLtoFile(imageSrc, `attendance-${Date.now()}.jpg`)

      const uploaded = await uploadFile(
        file,
        () => { },
        'CENTER_ATTENDANCE',
        'ATTENDANCE',
        true,
      )

      if (!uploaded?.url) throw new Error('Upload failed')

      const location = await getCurrentLocation()

      const lastRecord = attendanceData[0]
      const type =
        lastRecord?.checkIn && !lastRecord?.checkOut ? 'checkout' : 'checkin'

      await dispatch(
        checkInOrOutCenter({
          photo: uploaded.url,
          latitude: location.latitude,
          longitude: location.longitude,
          notes: note,
          type: type,
          createdAt: new Date().toISOString(),
        }),
      ).unwrap()

      enqueueSnackbar(
        `${type === 'checkin' ? 'Check In' : 'Check Out'} berhasil`,
        { variant: 'success' },
      )
      setNote('')
      dispatch(fetchCenterAttendance({}))
    } catch (error) {
      console.error(error)
      enqueueSnackbar(error.message || 'Gagal melakukan absensi', {
        variant: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  const handleSaveNote = async () => {
    if (!note.trim()) return
    try {
      setLoading(true)
      await dispatch(setCenterAttendanceNotes({ notes: note })).unwrap()
      setNote('')
      dispatch(fetchCenterAttendance({}))
      enqueueSnackbar('Catatan disimpan', { variant: 'success' })
    } catch (error) {
      enqueueSnackbar('Gagal menyimpan catatan', { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const handleLoadMore = () => {
    const nextPage = attendanceCurrentPage + 1
    dispatch(fetchCenterAttendance({ page: nextPage, size: 10 }))
  }

  // --- Helpers ---

  const dataURLtoFile = (dataurl, filename) => {
    const arr = dataurl.split(',')
    const mime = arr[0].match(/:(.*?);/)[1]
    const bstr = atob(arr[1])
    let n = bstr.length
    const u8arr = new Uint8Array(n)
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n)
    }
    return new File([u8arr], filename, { type: mime })
  }

  const getCurrentLocation = () => {
    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        ({ coords }) =>
          resolve({ latitude: coords.latitude, longitude: coords.longitude }),
        () => resolve({ latitude: '-', longitude: '-' }),
        { enableHighAccuracy: true },
      )
    })
  }

  // --- Renderers ---

  const renderEmployeeList = () => {
    return (
      <div
        style={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: '#f8f9fa',
        }}
      >
        {/* Header */}
        <div
          style={{
            backgroundColor: 'white',
            padding: '16px 24px',
            boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            position: 'sticky',
            top: 0,
            zIndex: 10,
          }}
        >
          <div>
            <h2
              style={{
                margin: 0,
                fontSize: '24px',
                fontWeight: 'bold',
                color: '#1f2937',
              }}
            >
              Absensi Center
            </h2>
            <p style={{ margin: 0, color: '#6b7280', fontSize: '14px' }}>
              Silakan pilih nama Anda untuk melakukan absensi
            </p>
          </div>
          <Button
            color="link"
            style={{ color: '#6b7280', padding: 0 }}
            onClick={handleExit}
            data-testid="center-attendance-exit-button"
          >
            <X size={28} />
          </Button>
        </div>

        {/* Search */}
        <div
          style={{
            padding: '16px 24px',
            backgroundColor: 'white',
            borderBottom: '1px solid #f3f4f6',
          }}
        >
          <div
            style={{
              position: 'relative',
              maxWidth: '500px',
              margin: '0 auto',
            }}
          >
            <Search
              size={20}
              style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#9ca3af',
              }}
            />
            <Input
              type="text"
              placeholder="Cari nama Anda..."
              style={{
                paddingLeft: '40px',
                borderRadius: '9999px',
                backgroundColor: '#f9fafb',
                border: '1px solid #e5e7eb',
              }}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              data-testid="center-attendance-search-input"
            />
          </div>
        </div>

        {/* Grid */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>
          {teamLoading ? (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '300px',
              }}
            >
              <Spinner color="primary" />
            </div>
          ) : (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                gap: '24px',
              }}
            >
              {team.map((emp) => (
                <div
                  key={emp.userId || emp.id}
                  onClick={() => handleSelectEmployee(emp)}
                  style={{
                    backgroundColor: 'white',
                    borderRadius: '16px',
                    padding: '24px',
                    cursor: 'pointer',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                    transition: 'all 0.3s ease',
                    border: '1px solid #f3f4f6',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                  }}
                  data-testid={`center-attendance-row-${emp.userId}`}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)'
                    e.currentTarget.style.boxShadow =
                      '0 12px 20px -8px rgba(0, 0, 0, 0.1)'
                    e.currentTarget.style.borderColor = '#bfdbfe'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'none'
                    e.currentTarget.style.boxShadow =
                      '0 2px 8px rgba(0,0,0,0.05)'
                    e.currentTarget.style.borderColor = '#f3f4f6'
                  }}
                >
                  <div
                    style={{
                      width: '96px',
                      height: '96px',
                      borderRadius: '50%',
                      backgroundColor: '#f9fafb',
                      marginBottom: '16px',
                      overflow: 'hidden',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '4px solid white',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                    }}
                  >
                    {emp.photo ? (
                      <img
                        src={emp.photo}
                        alt={emp.name}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                      />
                    ) : (
                      <User size={40} color="#9ca3af" />
                    )}
                  </div>
                  <h5
                    style={{
                      fontWeight: '700',
                      color: '#111827',
                      marginBottom: '8px',
                      fontSize: '16px',
                      textAlign: 'center',
                      lineHeight: '1.4',
                      display: '-webkit-box',
                      WebkitLineClamp: '2',
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {emp.name}
                  </h5>
                  <span
                    style={{
                      padding: '6px 16px',
                      backgroundColor: '#f3f4f6',
                      color: '#4b5563',
                      fontSize: '12px',
                      fontWeight: '500',
                      borderRadius: '9999px',
                      letterSpacing: '0.025em',
                    }}
                  >
                    {emp.role || 'Anggota'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    )
  }

  const renderFaceAuth = () => (
    <div
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'black',
      }}
    >
      {/* Header Overlay */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          padding: '16px',
          zIndex: 20,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background:
            'linear-gradient(to bottom, rgba(0,0,0,0.7), transparent)',
          color: 'white',
        }}
      >
        <Button color="link" style={{ color: 'white' }} onClick={handleBack}>
          <ArrowLeft size={24} />
        </Button>
        <div style={{ textAlign: 'center' }}>
          <h4 style={{ margin: 0, fontWeight: '600', fontSize: '18px' }}>
            Verifikasi Wajah Anda
          </h4>
          <p style={{ fontSize: '12px', opacity: 0.8, margin: 0 }}>
            {selectedEmployee?.name}
          </p>
        </div>
        <div style={{ width: '40px' }}></div>
      </div>

      {/* Camera View */}
      <div
        style={{
          flex: 1,
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        {image ? (
          <img
            src={image}
            alt="Captured"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <Webcam
            audio={false}
            ref={webcamRef}
            screenshotFormat="image/jpeg"
            mirrored={true}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            videoConstraints={{ facingMode: 'user' }}
          />
        )}

        {/* Scanning Overlay Effect */}
        {!image && !loading && (
          <>
            <div
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'none',
              }}
            >
              {/* SVG Face Guide */}
              <svg
                viewBox="0 0 200 250"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{
                  width: '100%',
                  height: '100%',
                  maxWidth: '500px',
                  maxHeight: '60vh',
                  opacity: 0.8,
                  filter: 'drop-shadow(0px 0px 4px rgba(0,0,0,0.5))',
                }}
              >
                <path
                  d="M100 20C60 20 30 50 30 90V110C30 140 50 165 75 175C60 185 40 200 30 230"
                  stroke="white"
                  strokeWidth="3"
                  strokeLinecap="round"
                />
                <path
                  d="M100 20C140 20 170 50 170 90V110C170 140 150 165 125 175C140 185 160 200 170 230"
                  stroke="white"
                  strokeWidth="3"
                  strokeLinecap="round"
                />
                {/* Dashed Frame */}
                <rect
                  x="10"
                  y="10"
                  width="180"
                  height="230"
                  rx="20"
                  stroke="white"
                  strokeWidth="1"
                  strokeDasharray="10 5"
                  opacity="0.5"
                />
              </svg>
            </div>
            <div
              style={{
                position: 'absolute',
                inset: 0,
                margin: '32px',
                border: '2px solid rgba(255,255,255,0.1)',
                borderRadius: '16px',
                pointerEvents: 'none',
                overflow: 'hidden',
              }}
            >
              <div
                className="animate-scan"
                style={{
                  width: '100%',
                  height: '2px',
                  backgroundColor: 'rgba(59, 130, 246, 0.6)',
                  position: 'absolute',
                  top: 0,
                  boxShadow: '0 0 10px rgba(59, 130, 246, 0.8)',
                }}
              ></div>
            </div>
          </>
        )}
      </div>

      {/* Controls */}
      <div
        style={{
          backgroundColor: 'black',
          padding: '32px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '32px',
        }}
      >
        {image ? (
          <>
            <Button
              outline
              style={{
                borderColor: 'white',
                color: 'white',
                borderRadius: '9999px',
                padding: '8px 24px',
              }}
              onClick={() => setImage(null)}
              disabled={loading}
              data-testid="center-attendance-capture-retry"
            >
              Ulangi
            </Button>
            {loading && <Spinner color="light" />}
          </>
        ) : (
          <button
            style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              backgroundColor: 'white',
              border: '4px solid #d1d5db',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
            onClick={handleCaptureAuth}
            disabled={loading}
            data-testid="center-attendance-capture-button"
          >
            {loading ? (
              <Spinner size="sm" color="dark" />
            ) : (
              <div
                style={{
                  width: '64px',
                  height: '64px',
                  borderRadius: '50%',
                  backgroundColor: '#2563eb',
                }}
              ></div>
            )}
          </button>
        )}
      </div>
    </div>
  )

  const renderDashboard = () => {
    const lastRecord = attendanceData[0]

    let statusText = 'Belum Absen'

    if (lastRecord) {
      if (lastRecord.checkIn && !lastRecord.checkOut) {
        statusText = 'Checked In'
      } else if (lastRecord.checkOut) {
        statusText = 'Checked Out'
      }
    }

    const isCheckedIn = statusText === 'Checked In'

    return (
      <div
        style={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: '#f8f9fa',
          minHeight: '100vh',
        }}
      >
        {/* Header */}
        <div
          style={{
            backgroundColor: 'white',
            padding: '16px 24px',
            boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            zIndex: 10,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                backgroundColor: '#e5e7eb',
                overflow: 'hidden',
              }}
            >
              {selectedEmployee?.photo ? (
                <img
                  src={selectedEmployee.photo}
                  alt=""
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : (
                <User style={{ padding: '8px', color: '#6b7280' }} />
              )}
            </div>
            <div>
              <h4
                style={{
                  margin: 0,
                  fontWeight: 'bold',
                  color: '#1f2937',
                  fontSize: '16px',
                }}
              >
                {selectedEmployee?.name}
              </h4>
              <span style={{ fontSize: '12px', color: '#6b7280' }}>
                Dashboard Absensi
              </span>
            </div>
          </div>
          <Button
            color="danger"
            style={{
              borderRadius: '9999px',
              padding: '8px 24px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
            onClick={handleBack}
            data-testid="center-attendance-dashboard-exit-button"
          >
            Keluar
          </Button>
        </div>

        {/* Content - Full Height */}
        <div
          style={{
            flex: 1,
            padding: '24px',
            display: 'flex',
            flexDirection: 'column',
          }}
          className="dashboard-container"
        >
          <div
            style={{
              flex: 1,
              width: '100%',
              maxWidth: '1400px',
              margin: '0 auto',
            }}
          >
            <Row className="dashboard-row">
              <Col
                md={6}
                className="dashboard-col"
                style={{ paddingBottom: '0' }}
              >
                {/* Camera Card - Flex Column to fill height */}
                <Card
                  style={{
                    overflow: 'hidden',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                    border: 'none',
                    borderRadius: '16px',
                    height: '100%',
                    maxHeight: '800px',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <div
                    style={{
                      flex: 1,
                      position: 'relative',
                      backgroundColor: 'black',
                      minHeight: 0,
                    }}
                  >
                    <Webcam
                      audio={false}
                      ref={webcamRef}
                      screenshotFormat="image/jpeg"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                      videoConstraints={{ facingMode: 'user' }}
                      mirrored={true}
                    />
                    <div
                      style={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        pointerEvents: 'none',
                        paddingBottom: '80px',
                      }}
                    >
                      {/* SVG Face Guide */}
                      <svg
                        viewBox="0 0 200 250"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        style={{
                          width: '100%',
                          height: '100%',
                          maxWidth: '400px',
                          maxHeight: '50vh',
                          opacity: 0.6,
                          filter: 'drop-shadow(0px 0px 4px rgba(0,0,0,0.5))',
                        }}
                      >
                        <path
                          d="M100 20C60 20 30 50 30 90V110C30 140 50 165 75 175C60 185 40 200 30 230"
                          stroke="white"
                          strokeWidth="3"
                          strokeLinecap="round"
                        />
                        <path
                          d="M100 20C140 20 170 50 170 90V110C170 140 150 165 125 175C140 185 160 200 170 230"
                          stroke="white"
                          strokeWidth="3"
                          strokeLinecap="round"
                        />
                        {/* Dashed Frame */}
                        <rect
                          x="10"
                          y="10"
                          width="180"
                          height="230"
                          rx="20"
                          stroke="white"
                          strokeWidth="1"
                          strokeDasharray="10 5"
                          opacity="0.5"
                        />
                      </svg>
                    </div>
                    <div
                      style={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        padding: '24px',
                        background:
                          'linear-gradient(to top, rgba(0,0,0,0.8), transparent)',
                        display: 'flex',
                        justifyContent: 'center',
                      }}
                    >
                      <Button
                        color={isCheckedIn ? 'danger' : 'success'}
                        size="lg"
                        style={{
                          width: '100%',
                          padding: '16px',
                          borderRadius: '16px',
                          fontWeight: 'bold',
                          fontSize: '18px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '12px',
                          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                        }}
                        onClick={handleCheckInOut}
                        disabled={loading}
                      >
                        {loading ? (
                          <Spinner size="sm" />
                        ) : isCheckedIn ? (
                          <LogOut size={24} />
                        ) : (
                          <CheckCircle size={24} />
                        )}
                        {isCheckedIn ? 'Check Out' : 'Check In'}
                      </Button>
                    </div>
                  </div>
                  <CardBody
                    style={{
                      backgroundColor: 'white',
                      padding: '24px',
                      flex: '0 0 auto',
                    }}
                  >
                    <h6
                      style={{
                        fontWeight: '600',
                        marginBottom: '12px',
                        color: '#374151',
                      }}
                    >
                      Catatan Harian
                    </h6>
                    <div style={{ display: 'flex', gap: '12px' }}>
                      <Input
                        type="text"
                        placeholder="Tulis catatan aktivitas..."
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        style={{
                          backgroundColor: '#f9fafb',
                          borderColor: '#e5e7eb',
                          padding: '12px',
                          borderRadius: '12px',
                        }}
                      />
                      <Button
                        color="primary"
                        style={{
                          padding: '12px 32px',
                          borderRadius: '12px',
                          fontWeight: '600',
                        }}
                        onClick={handleSaveNote}
                        disabled={loading || !note.trim() || !isCheckedIn}
                      >
                        Kirim
                      </Button>
                    </div>
                  </CardBody>
                </Card>
              </Col>

              <Col
                md={6}
                className="dashboard-col"
                style={{ paddingBottom: '0' }}
              >
                {/* History List - Flex Column to fill height */}
                <div
                  style={{
                    backgroundColor: 'white',
                    borderRadius: '16px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    padding: '24px',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <h5
                    style={{
                      fontWeight: 'bold',
                      color: '#1f2937',
                      marginBottom: '24px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      flexShrink: 0,
                    }}
                  >
                    <div
                      style={{
                        padding: '8px',
                        backgroundColor: '#eff6ff',
                        borderRadius: '8px',
                        color: '#3b82f6',
                      }}
                    >
                      <Clock size={20} />
                    </div>
                    Riwayat Absensi
                  </h5>

                  <div
                    style={{ flex: 1, overflowY: 'auto', paddingRight: '8px' }}
                  >
                    {attendanceLoading ? (
                      <div style={{ textAlign: 'center', padding: '32px' }}>
                        <Spinner color="primary" />
                      </div>
                    ) : attendanceData.length === 0 ? (
                      <div
                        style={{
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#9ca3af',
                          padding: '48px',
                          backgroundColor: '#f9fafb',
                          borderRadius: '16px',
                          border: '2px dashed #e5e7eb',
                        }}
                      >
                        <Clock
                          size={48}
                          style={{ marginBottom: '16px', opacity: 0.5 }}
                        />
                        <p style={{ fontSize: '16px', fontWeight: '500' }}>
                          Belum ada riwayat absensi hari ini
                        </p>
                      </div>
                    ) : (
                      attendanceData.map((item, idx) => (
                        <div
                          key={idx}
                          style={{
                            backgroundColor: '#f9fafb',
                            borderRadius: '16px',
                            padding: '20px',
                            marginBottom: '16px',
                            border: '1px solid #f3f4f6',
                            transition: 'all 0.2s',
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              gap: '16px',
                              marginBottom: '16px',
                            }}
                          >
                            <div style={{ flex: 1 }}>
                              <div
                                className="d-flex align-items-center justify-content-between mb-1"
                                style={{ minHeight: '16px' }}
                              >
                                <span
                                  style={{
                                    fontSize: '11px',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em',
                                    fontWeight: '700',
                                    color: '#16a34a',
                                  }}
                                >
                                  Check In
                                </span>
                                <span
                                  style={{
                                    fontSize: '10px',
                                    fontWeight: '600',
                                    color: '#6b7280',
                                  }}
                                >
                                  {moment(
                                    item.checkIn || item.createdAt,
                                  ).format('DD MMM YYYY')}
                                </span>
                              </div>
                              <div
                                style={{
                                  display: 'flex',
                                  gap: '8px',
                                  alignItems: 'center',
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: '20px',
                                    fontFamily: 'monospace',
                                    fontWeight: '700',
                                    color: '#111827',
                                    backgroundColor: 'white',
                                    padding: '12px',
                                    borderRadius: '12px',
                                    border: '1px solid #e5e7eb',
                                    textAlign: 'center',
                                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                                    flex: 1,
                                  }}
                                >
                                  {item.checkIn
                                    ? moment(item.checkIn).format('HH:mm')
                                    : '--:--'}
                                </div>
                                {item.photoIn && (
                                  <Button
                                    outline
                                    color="secondary"
                                    size="sm"
                                    style={{
                                      padding: '8px',
                                      borderRadius: '8px',
                                      borderColor: '#d1d5db',
                                      color: '#6b7280',
                                    }}
                                    onClick={() =>
                                      setPhotoModal({
                                        isOpen: true,
                                        photoUrl: item.photoIn,
                                        type: 'Check In',
                                      })
                                    }
                                  >
                                    <Image size={16} />
                                  </Button>
                                )}
                              </div>
                            </div>
                            <div style={{ flex: 1 }}>
                              <div
                                className="d-flex align-items-center justify-content-between mb-1"
                                style={{ minHeight: '16px' }}
                              >
                                <span
                                  style={{
                                    fontSize: '11px',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em',
                                    fontWeight: '700',
                                    color: '#dc2626',
                                  }}
                                >
                                  Check Out
                                </span>
                                {item.checkOut && (
                                  <span
                                    style={{
                                      fontSize: '10px',
                                      fontWeight: '600',
                                      color: '#6b7280',
                                    }}
                                  >
                                    {moment(item.checkOut).format(
                                      'DD MMM YYYY',
                                    )}
                                  </span>
                                )}
                              </div>
                              <div
                                style={{
                                  display: 'flex',
                                  gap: '8px',
                                  alignItems: 'center',
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: '20px',
                                    fontFamily: 'monospace',
                                    fontWeight: '700',
                                    color: '#111827',
                                    backgroundColor: 'white',
                                    padding: '12px',
                                    borderRadius: '12px',
                                    border: '1px solid #e5e7eb',
                                    textAlign: 'center',
                                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                                    flex: 1,
                                  }}
                                >
                                  {item.checkOut
                                    ? moment(item.checkOut).format('HH:mm')
                                    : '--:--'}
                                </div>
                                {item.photoOut && (
                                  <Button
                                    outline
                                    color="secondary"
                                    size="sm"
                                    style={{
                                      padding: '8px',
                                      borderRadius: '8px',
                                      borderColor: '#d1d5db',
                                      color: '#6b7280',
                                    }}
                                    onClick={() =>
                                      setPhotoModal({
                                        isOpen: true,
                                        photoUrl: item.photoOut,
                                        type: 'Check Out',
                                      })
                                    }
                                  >
                                    <Image size={16} />
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                          {item.notes && item.notes.length > 0 && (
                            <div style={{ marginTop: '12px' }}>
                              {item.notes.map((noteItem, noteIdx) => (
                                <div
                                  key={noteIdx}
                                  style={{
                                    backgroundColor: '#fffbeb',
                                    padding: '8px 12px',
                                    borderRadius: '8px',
                                    fontSize: '12px',
                                    color: '#92400e',
                                    border: '1px solid #fcd34d',
                                    display: 'flex',
                                    gap: '8px',
                                    alignItems: 'center',
                                    marginBottom:
                                      noteIdx < item.notes.length - 1
                                        ? '6px'
                                        : '0',
                                  }}
                                >
                                  <div>📝</div>
                                  <div
                                    style={{
                                      fontSize: '11px',
                                      fontWeight: '600',
                                      opacity: 0.7,
                                      minWidth: '40px',
                                    }}
                                  >
                                    {moment(noteItem.ts).format('HH:mm')}
                                  </div>
                                  <div style={{ flex: 1, lineHeight: '1.4' }}>
                                    "{noteItem.notes}"
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))
                    )}

                    {/* Load More Button */}
                    {attendanceHasMore && !attendanceLoading && (
                      <div style={{ textAlign: 'center', marginTop: '16px' }}>
                        <Button
                          color="primary"
                          outline
                          style={{
                            borderRadius: '12px',
                            padding: '12px 32px',
                            fontWeight: '600',
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                          }}
                          onClick={handleLoadMore}
                          disabled={attendanceLoading}
                        >
                          {attendanceLoading ? (
                            <>
                              <Spinner size="sm" />
                              Loading...
                            </>
                          ) : (
                            <>
                              <Clock size={16} />
                              Load More
                            </>
                          )}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </Col>
            </Row>
          </div>
        </div>
      </div>
    )
  }

  // Fullscreen Overlay Container using Portal
  return createPortal(
    <div
      className="center-attendance-root"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 99999,
        backgroundColor: '#f8f9fa',
        overflow: 'auto',
        overflowY: 'auto',
        overflowX: 'hidden',
        WebkitOverflowScrolling: 'touch',
        fontFamily: 'sans-serif',
        height: '100vh',
        width: '100vw',
      }}
    >
      {step === STEP_EMPLOYEE_LIST && renderEmployeeList()}
      {step === STEP_FACE_AUTH && renderFaceAuth()}
      {step === STEP_DASHBOARD && renderDashboard()}

      {/* Custom Toast Notification */}
      {/* {toast && (
        <div
          style={{
            position: 'fixed',
            top: '24px',
            right: '24px',
            zIndex: 999999,
          }}
        >
          <CustomToast
            message={toast.message}
            variant={toast.variant}
            onClose={() => setToast(null)}
          />
        </div>
      )} */}

      {/* Photo Modal */}
      {photoModal.isOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            zIndex: 999999,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
          }}
          onClick={() =>
            setPhotoModal({ isOpen: false, photoUrl: '', type: '' })
          }
        >
          <div
            style={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              color: 'white',
              cursor: 'pointer',
              fontSize: '32px',
              fontWeight: 'bold',
              zIndex: 1000000,
            }}
            onClick={() =>
              setPhotoModal({ isOpen: false, photoUrl: '', type: '' })
            }
          >
            <X size={32} />
          </div>
          <div
            style={{
              textAlign: 'center',
              color: 'white',
              marginBottom: '20px',
              fontSize: '20px',
              fontWeight: '600',
            }}
          >
            {photoModal.type} Photo
          </div>
          <img
            src={photoModal.photoUrl}
            alt={`${photoModal.type}`}
            style={{
              maxWidth: '90%',
              maxHeight: '80vh',
              objectFit: 'contain',
              borderRadius: '8px',
            }}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* Styles moved to center-attendance.css */}
    </div>,
    document.body,
  )
}
